/**
 * Living help — the compiled shape of one screen's help (ADR-0066).
 *
 * <p>`help/screens/<route>.md` (YAML front matter + six fixed sections) is compiled by
 * `scripts/help-build.mjs` into `frontend/public/help-static/<route>.json` with exactly this
 * shape. The panel fetches it by route; the Markdown of each section is rendered at runtime by
 * `shared/help/markdown/renderHelpMarkdown.ts` (marked + DOMPurify), so customer notes (Wave 3)
 * share the renderer. The gap list is also emitted to `backend/src/main/resources/help-gaps.json`
 * for `HelpBootstrap` (John's lane) as {@link HelpGapExport}.
 *
 * <p>Keep this file dependency-free: the build script mirrors it in plain JS and
 * `help/schema.json` validates the front matter against the same field names.
 */

export type HelpStatus = 'draft' | 'sme-review' | 'published';

export type HelpGapStatus = 'open' | 'answered' | 'resolved' | 'rejected';

/** One-line help for a control; keyed by the control's `data-help-id` (unique within the screen). */
export interface HelpField {
  label: string;
  help: string;
}

export interface HelpGap {
  id: string;
  question: string;
  /** Section anchor the gap blocks (`purpose`, `before-you-start`, `how-to`, `fields`, `rules`, `related`). */
  section: string;
  status: HelpGapStatus;
}

export interface HelpTourStep {
  /** `data-help-id` on this screen. */
  ref: string;
  text: string;
}

export interface HelpTour {
  id: string;
  title: string;
  steps: HelpTourStep[];
}

/** A `##` or `###` section of the help file; `anchor` is the heading slug (stable, customer notes attach to it). */
export interface HelpSection {
  anchor: string;
  heading: string;
  level: 2 | 3;
  /** Raw Markdown body (no heading line); rendered at runtime. */
  markdown: string;
  /**
   * Sibling-tab section (decision Q163): the later tab of this screen's tab group that this `###`
   * documents, from a heading ending in `{tab=/browse/<key>}` (stripped from `heading`). The panel
   * opened on that tab scrolls here (`helpTabGroups.ts`).
   */
  tab?: string;
}

export interface HelpScreenDoc {
  /** Catalog `page_route`, e.g. `/browse/wave-status`. */
  route: string;
  title: string;
  pbWindow: string;
  /** `dbxx.window_modules.help_ref` value, when PB had a CHM topic for this window. */
  helpRef?: string;
  versionIntroduced?: string;
  status: HelpStatus;
  fields: Record<string, HelpField>;
  gaps: HelpGap[];
  tours: HelpTour[];
  sections: HelpSection[];
  /** Routes named in the Related screens section (`[[/route]]`). */
  related: string[];
}

/** One row of `frontend/public/help-static/index.json`. */
export interface HelpIndexEntry {
  route: string;
  title: string;
  status: HelpStatus;
  openGaps: number;
}

/**
 * One row of `backend/src/main/resources/help-gaps.json` — the shape the backend's
 * `HelpGapFileEntry` reads (John, requests.md 2026-10-10). `status` is copied verbatim from the
 * front matter; the backend treats anything but `resolved` as open. `priority` is optional and
 * not emitted by the build yet (the schema has no such field; the backend falls back to votes).
 */
export interface HelpGapExport {
  id: string;
  route: string;
  section: string;
  question: string;
  status: HelpGapStatus;
  priority?: number;
}

/** Element-ref grammar shared by the renderer, the lint and the highlight provider. */
export const HELP_REF_PATTERN = /\[([^\]]+)\]\{((?:\/[^#}\s]*#)?[a-z][a-z0-9-]*)\}/g;
export const HELP_GAP_PATTERN = /\{\{gap:([a-z0-9-]+)\}\}/g;
export const HELP_SCREEN_LINK_PATTERN = /\[\[(\/[^\]\s]+)\]\]/g;

/** `data-help-id` prefixes; lint warns on anything else. */
export const HELP_ID_PREFIXES = ['btn-', 'field-', 'grid-', 'col-', 'tab-', 'menu-'] as const;

/** Path of a screen's compiled help under `/help-static/`. `/browse/wave-status` → `browse/wave-status.json`. */
export function helpStaticPath(route: string): string {
  return `${route.replace(/^\//, '')}.json`;
}
